import { useParams, usePathname } from 'next/navigation';

// Lib
import { formatUrlKeys, useProfileHasAccessPage } from '@/utils/hooks/acl/useProfileHasAccessPage';
import useDisplayFirstStatsPanel from '@/utils/hooks/firstStats/useDisplayFirstStatsPanel';
// Utils
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { getEmptyStateName } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/utils/getEmptyStateName';

// Types
import type { SocialProfileNetworkType } from '@/types/socialProfileType';

import { networkHasAccess } from '@/lib/acl/pageHasAccess';

// Components
import FirstStats from '@/components/composite/FirstStats';
import { GroupEmptyState } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/GroupEmptyState';
import { MultiProfilesEmptyState } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/MultiProfilesEmptyState';
import { SocialProfileEmptyState } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/SocialProfileEmptyState';

type PageContentWrapperProps = {
    children: React.ReactNode;
};

const allNetworksAvailable: SocialProfileNetworkType[] = [
    'facebook',
    'instagram',
    'linkedin',
    'tiktok_business',
    'pinterest'
];

export function PageContentWrapper({ children }: PageContentWrapperProps) {
    const params = useParams();
    const pathname = usePathname();
    const { activeSocialProfile } = useActiveSocialProfile();

    const { hideFirstStatsEmptyState, setHideFirstStatsEmptyState } = useDisplayFirstStatsPanel();

    const {
        activeSocialProfileNoAccess,
        activeGroupNoAccess,
        activeMultiSelectionProfilesNoAccess
    } = useProfileHasAccessPage();

    const networkHasAccessFormatted = formatUrlKeys(networkHasAccess, params);
    const emptyStateName = getEmptyStateName(pathname);
    const isPublishingAndNotFeedPreview =
        pathname?.includes('/scheduler') && !pathname?.includes('/feed-preview');

    if (activeSocialProfileNoAccess) {
        return (
            <SocialProfileEmptyState
                supportedNetworkTypes={networkHasAccessFormatted[pathname]}
                emptyStateName={emptyStateName}
                socialProfileNetworkType={activeSocialProfile.social_network_type}
            />
        );
    }

    if (activeGroupNoAccess) {
        return (
            <GroupEmptyState
                supportedNetworkTypes={
                    networkHasAccessFormatted?.[pathname] ?? allNetworksAvailable
                }
                emptyStateName={isPublishingAndNotFeedPreview ? null : emptyStateName}
            />
        );
    }

    if (activeMultiSelectionProfilesNoAccess) {
        return (
            <MultiProfilesEmptyState
                supportedNetworkTypes={
                    networkHasAccessFormatted?.[pathname] ?? allNetworksAvailable
                }
                emptyStateName={isPublishingAndNotFeedPreview ? null : emptyStateName}
            />
        );
    }

    if (!hideFirstStatsEmptyState) {
        getEmptyStateName(pathname);
        return (
            <div className="overflow-hidden rounded-md bg-white dark:bg-icoDarkMode-anthracite">
                <FirstStats start={() => setHideFirstStatsEmptyState(true)} />
            </div>
        );
    }

    return children;
}
